import {
  Alerta,
  Alternador,
  Botao,
  Caixa,
  Campo,
  Cartao,
  Entrada,
  EstadoVazio,
  Modal,
  TabelaDados,
  useAvisos,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { chaves } from '../../api/chaves'
import type {
  EntradaAtualizarUnidade,
  EntradaCriarUnidade,
  Unidade,
} from '@baiocchi/shared'
import { unidadesApi } from '../../api/organizacao'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeEditar, IconeMais } from '../../componentes/icones'
import { CabecalhoPagina } from '../../layout/layout-app'

/**
 * TELA-23 — Unidades. **[NOVA]** em relação ao legado, onde as unidades eram fixas.
 *
 * Existe porque unidade é a raiz do escopo de dados: o gestor enxerga por unidade e
 * a equipe pertence a uma. Unidade fixa em código significaria alterar o escopo de
 * segurança por deploy.
 *
 * Restrita ao Administrador (`UNIDADE_GERENCIAR`).
 */
export function PaginaUnidades() {
  const { pode } = useSessao()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const [emEdicao, setEmEdicao] = useState<Unidade | 'novo' | null>(null)
  const [mostrarInativas, setMostrarInativas] = useState(false)

  const podeGerenciar = pode('UNIDADE_GERENCIAR')
  const filtros = { limite: 200, ordenar: 'ordem', ...(mostrarInativas ? {} : { ativo: 'true' }) }

  const consulta = useQuery({
    queryKey: chaves.organizacao.unidades(filtros),
    queryFn: ({ signal }) => unidadesApi.listar(filtros, signal),
    retry: false,
  })

  const alternar = useMutation({
    mutationFn: (unidade: Unidade) =>
      unidade.ativo ? unidadesApi.desativar(unidade.id) : unidadesApi.reativar(unidade.id),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['organizacao'] })
      avisos.sucesso('Status atualizado')
    },
    onError: (erro) => avisos.erro('Não foi possível alterar', erro.message),
  })

  const colunas: ReadonlyArray<ColunaTabela<Unidade>> = [
    {
      chave: 'id',
      cabecalho: 'Id',
      classe: 'w-16 tabular-nums',
      celula: (unidade) => <span className="text-xs text-conteudo-fraco">{unidade.id}</span>,
    },
    {
      chave: 'titulo',
      cabecalho: 'Título',
      celula: (unidade) => <span className="font-medium">{unidade.titulo}</span>,
    },
    {
      chave: 'ordem',
      cabecalho: 'Ordem',
      alinhamento: 'centro',
      classe: 'w-20 tabular-nums',
      celula: (unidade) => <span className="text-xs">{unidade.ordem}</span>,
    },
    {
      chave: 'ativo',
      cabecalho: 'Status',
      alinhamento: 'centro',
      classe: 'w-28',
      celula: (unidade) => (
        <Alternador
          marcado={unidade.ativo}
          desabilitado={!podeGerenciar || alternar.isPending}
          onAlterar={() => alternar.mutate(unidade)}
          rotulo={unidade.ativo ? 'Ativa' : 'Inativa'}
          className="justify-center text-xs"
        />
      ),
    },
    {
      chave: 'acoes',
      cabecalho: '',
      alinhamento: 'direita',
      classe: 'w-16',
      celula: (unidade) => (
        <Botao
          variante="fantasma"
          tamanho="icone"
          aria-label={`Editar ${unidade.titulo}`}
          disabled={!podeGerenciar}
          onClick={() => setEmEdicao(unidade)}
        >
          <IconeEditar className="h-4 w-4" />
        </Botao>
      ),
    },
  ]

  return (
    <>
      <CabecalhoPagina
        titulo="Unidades"
        descricao="Unidade é a raiz do escopo de dados: o gestor enxerga por unidade e cada equipe pertence a uma."
        acoes={
          podeGerenciar ? (
            <Botao
              onClick={() => setEmEdicao('novo')}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Nova unidade
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-4">
        <Caixa
          rotulo="Mostrar unidades desativadas"
          checked={mostrarInativas}
          onChange={(evento) => setMostrarInativas(evento.target.checked)}
        />
      </Cartao>

      <Cartao semPadding>
        <TabelaDados
          colunas={colunas}
          itens={consulta.data?.dados ?? []}
          chaveDe={(unidade) => String(unidade.id)}
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro
                erro={consulta.error}
                aoTentarNovamente={() => void consulta.refetch()}
              />
            ) : undefined
          }
          realce={(unidade) => !unidade.ativo}
          vazio={<EstadoVazio titulo="Nenhuma unidade cadastrada" />}
        />
      </Cartao>

      <ModalUnidade unidade={emEdicao} onFechar={() => setEmEdicao(null)} />
    </>
  )
}

function ModalUnidade({
  unidade,
  onFechar,
}: {
  unidade: Unidade | 'novo' | null
  onFechar: () => void
}) {
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const ehNova = unidade === 'novo'
  const registro = unidade !== 'novo' && unidade !== null ? unidade : null

  const [estado, setEstado] = useState({ titulo: '', ordem: 0 })
  const [erro, setErro] = useState<string | null>(null)

  // Carrega o formulário quando o modal ABRE, e a dependência é a identidade do
  // registro em edição — não os campos dele. Incluir `registro?.titulo` e companhia
  // faria o efeito rodar a cada `refetch` da consulta e **sobrescrever o que o
  // usuário está digitando**.
  useEffect(() => {
    if (unidade === null) return
    setErro(null)
    setEstado({
      titulo: registro?.titulo ?? '',
      ordem: registro?.ordem ?? 0,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidade])

  /**
   * `ativo` **não** entra no corpo: ativar e desativar têm rota própria
   * (`POST /unidades/{id}/desativar` e `/reativar`), porque desativar confere o que
   * está vinculado (`409 ORGANIZACAO_EM_USO`) e audita. O schema compartilhado é
   * `.strict()` e recusava o corpo inteiro com `422` — a unidade não era criada.
   *
   * As anotações de tipo abaixo são o que faz o TypeScript recusar campo a mais:
   * excesso de propriedade só é conferido em literal anotado, e não em variável
   * solta passada adiante — foi assim que o campo passou despercebido.
   */
  const salvar = useMutation({
    mutationFn: () => {
      if (ehNova) {
        const corpo: EntradaCriarUnidade = { titulo: estado.titulo.trim(), ordem: estado.ordem }
        return unidadesApi.criar(corpo)
      }
      const corpo: EntradaAtualizarUnidade = {
        titulo: estado.titulo.trim(),
        ordem: estado.ordem,
      }
      return unidadesApi.atualizar(registro?.id as number, corpo)
    },
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['organizacao'] })
      avisos.sucesso(ehNova ? 'Unidade criada' : 'Unidade atualizada')
      onFechar()
    },
    onError: (causa) => setErro(causa instanceof Error ? causa.message : 'Não foi possível salvar.'),
  })

  return (
    <Modal
      aberto={unidade !== null}
      onFechar={onFechar}
      travado={salvar.isPending}
      titulo={ehNova ? 'Nova unidade' : `Editar ${registro?.titulo ?? ''}`}
      largura="sm"
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={salvar.isPending}>
            Fechar
          </Botao>
          <Botao
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={estado.titulo.trim().length < 2}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Alerta tom="perigo">{erro}</Alerta> : null}

        <Campo rotulo="Título" obrigatorio>
          {(atributos) => (
            <Entrada
              {...atributos}
              autoFocus
              placeholder="Ex.: Aluguel"
              value={estado.titulo}
              onChange={(evento) => setEstado((atual) => ({ ...atual, titulo: evento.target.value }))}
            />
          )}
        </Campo>

        <Campo rotulo="Ordem">
          {(atributos) => (
            <Entrada
              {...atributos}
              type="number"
              min={0}
              value={String(estado.ordem)}
              onChange={(evento) =>
                setEstado((atual) => ({ ...atual, ordem: Number(evento.target.value) }))
              }
            />
          )}
        </Campo>

        {/*
          Sem caixa "Ativa" aqui de propósito: o status muda pelo alternador da
          coluna Status, que chama `desativar`/`reativar`. Uma segunda via nesta
          modal daria dois caminhos para a mesma mudança de estado, um deles sem a
          conferência de vínculos.
        */}
      </div>
    </Modal>
  )
}
